import { useCallback, useEffect, useState } from 'react';
import { ModelViewerElement } from '@google/model-viewer';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';

import { Units } from '../../../../../../../types';
import { ModelViewerConfig } from '../../../../../../../helpers/ModelViewer/ModelViewer.types';
import { ModelViewerItem } from './useModelViewerEditor.types';

import { useModelViewerDropZone } from '../useModelViewerDropZone';
import { useModelViewerToggleNeutralMaterial } from '../useModelViewerToggleNeutralMaterial';
import { useModelViewerMeshEdges } from '../useModelViewerMeshEdges';
import { useModelViewerBackground } from '../useModelViewerBackground';
import { useModelViewerAspectRatio } from '../useModelViewerAspectRatio';

import { useLocalStorageProxyState } from '../../../../../../../common/hooks/useLocalStorageProxyState';
import { modelViewerLocalStorageKey } from '../../../../../../../helpers/ModelViewer/modelViewerConstants';

interface ModelViewerEditorOptions {
  model?: ModelViewerItem;
  initialModelIndex?: number;
  models?: ModelViewerItem[];
}

function useModelViewerEditor({
  model,
  initialModelIndex,
  models
}: ModelViewerEditorOptions) {
  const {
    isDragging,
    currentModel,
    fileInput,
    dropZoneRootEl,
    dropError,
    handleDropClick,
    handleDropInputChange,
    handleDrop,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    setCurrentModelExternal
  } = useModelViewerDropZone();

  const [activeItemIndex, setActiveItem] = useState(initialModelIndex || 0);
  const [withDimensions, setWithDimensions] = useState(false);
  const [withRuler, setWithRuler] = useState(false);
  const [withGridAxesHelper, setWithGridAxesHelper] = useState(false);
  const [modelViewerConfig, setModelViewerConfig] =
    useLocalStorageProxyState<ModelViewerConfig>(modelViewerLocalStorageKey, {
      units: Units.CM
    });
  const [modelViewerEl, setModelViewerEl] = useState<ModelViewerElement | null>(
    null
  );
  const [isPaused, setIsPaused] = useState<boolean>(true);
  const [withAvailableAnimations, setWithAvailableAnimations] =
    useState<boolean>(false);

  const handlePrevItem = useCallback(() => {
    setActiveItem((prevActiveSlide) =>
      prevActiveSlide > 0 ? prevActiveSlide - 1 : models?.length - 1
    );
  }, [models?.length]);

  const handleNextItem = useCallback(() => {
    setActiveItem((prevActiveSlide) =>
      prevActiveSlide < models?.length - 1 ? prevActiveSlide + 1 : 0
    );
  }, [models?.length]);

  const activeItem =
    currentModel || (isArray(models) ? models[activeItemIndex] : model);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target === modelViewerEl) return;

      switch (e.key) {
        case 'ArrowLeft':
          handlePrevItem();
          break;

        case 'ArrowRight':
          handleNextItem();
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [handleNextItem, handlePrevItem, modelViewerEl]);

  const toggleWithDimensions = useCallback(
    () => setWithDimensions((v) => !v),
    []
  );

  const toggleWithRuler = useCallback(() => setWithRuler((v) => !v), []);

  const toggleWithGridAxesHelper = useCallback(
    () => setWithGridAxesHelper((v) => !v),
    []
  );

  const toggleUnits = useCallback(
    () =>
      setModelViewerConfig({
        ...modelViewerConfig,
        units: modelViewerConfig.units === Units.CM ? Units.IN : Units.CM
      }),
    [modelViewerConfig, setModelViewerConfig]
  );

  const setUnits = useCallback(
    (units: Units) => setModelViewerConfig({ ...modelViewerConfig, units }),
    [modelViewerConfig, setModelViewerConfig]
  );

  const toggleAnimationState = useCallback(() => {
    isPaused ? modelViewerEl?.play() : modelViewerEl?.pause();
  }, [isPaused, modelViewerEl]);

  useEffect(() => {
    const onAnimationStateChanged = () => {
      setIsPaused(modelViewerEl?.paused ?? true);
    };

    modelViewerEl?.addEventListener('play', onAnimationStateChanged);
    modelViewerEl?.addEventListener('pause', onAnimationStateChanged);
    onAnimationStateChanged();

    return () => {
      modelViewerEl?.removeEventListener('play', onAnimationStateChanged);
      modelViewerEl?.removeEventListener('pause', onAnimationStateChanged);
    };
  }, [modelViewerEl]);

  const [isModelLoaded, setIsModelLoaded] = useState(false);

  useEffect(() => {
    setIsModelLoaded(false);

    const onLoad = () => {
      setIsModelLoaded(true);
      const hasAnimations = !isEmpty(modelViewerEl?.availableAnimations || []);
      setWithAvailableAnimations(hasAnimations);
    };

    if (modelViewerEl?.loaded) {
      setIsModelLoaded(true);
    } else {
      modelViewerEl?.addEventListener('load', onLoad);
    }

    return () => {
      modelViewerEl?.removeEventListener('load', onLoad);
    };
  }, [modelViewerEl, activeItem?.file]);

  const { isNeutralMode, toggleNeutralMaterial } =
    useModelViewerToggleNeutralMaterial({ activeItem, modelViewerEl });

  const { meshEdgesMode, cycleMeshEdgesMode, setMeshEdgesMode } =
    useModelViewerMeshEdges();

  const backgroundData = useModelViewerBackground();
  const aspectRatioData = useModelViewerAspectRatio();

  return {
    multiplyItems: size(models) > 1,
    modelViewerEl,
    setModelViewerEl,
    activeItem,
    handlePrevItem,
    handleNextItem,
    activeItemIndex,
    withDimensions,
    toggleWithDimensions,
    withRuler,
    toggleWithRuler,
    setWithRuler,
    units: modelViewerConfig.units,
    toggleUnits,
    setUnits,
    withGridAxesHelper,
    toggleWithGridAxesHelper,
    toggleAnimationState,
    withAvailableAnimations,
    isPaused,
    isDragging,
    fileInput,
    dropZoneRootEl,
    dropError,
    handleDropClick,
    handleDropInputChange,
    handleDrop,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    setCurrentModelExternal,
    // A dropped file or a Vymir export replaced the product's own model.
    isModelReplaced: !!currentModel,
    isNeutralMode,
    toggleNeutralMaterial,
    meshEdgesMode,
    cycleMeshEdgesMode,
    setMeshEdgesMode,
    isModelLoaded,
    ...aspectRatioData,
    ...backgroundData
  };
}

export default useModelViewerEditor;
